import {
	CurrencyType,
	type UserSettingsDto,
	UserSettingsDtoContainerStatus,
	WeightUnit,
} from '@uturn/api/v1';
import type { TransportTypeCode } from '@uturn/portal/types/shipment';
import type { Item } from '../components/select-editor';
import { getDefaultLocationData } from './locations';

export type ShippingLineOption = Item & { scacCode?: string };

/**
 * `shippingLineId` is `-1` when the user explicitly picked "no shipping line"
 * and `null`/`undefined` when they have no preference. Neither prefills a value.
 */
const findDefaultShippingLine = (
	shippingLines: ShippingLineOption[],
	shippingLineId: number | null | undefined,
) => {
	if (shippingLineId === undefined || shippingLineId === null) {
		return undefined;
	}

	return shippingLines.find(
		(shippingLine) => shippingLine.value === String(shippingLineId),
	);
};

/**
 * Default cell values every (untouched) row starts with.
 *
 * The user's prefilled values ("Change defaults") are folded in here, so bulk
 * create starts from the same defaults as manual create
 */
export const getDefaultRowData = (
	transportType: TransportTypeCode,
	userSettings?: UserSettingsDto,
	shippingLines: ShippingLineOption[] = [],
) => {
	const defaultShippingLine = findDefaultShippingLine(
		shippingLines,
		userSettings?.shippingLineId,
	);
	const isEmptyContainer =
		userSettings?.containerStatus === UserSettingsDtoContainerStatus.EMPTY;

	const DEFAULT_ROW_DATA = {
		cargoGrossWeight: {
			value: {
				unit: WeightUnit.kilogram,
				...(isEmptyContainer && { quantity: 0 }),
			},
		},
		price: {
			value: {
				currency: CurrencyType.EUR,
			},
		},
		vat: { value: userSettings?.requiresVat ?? false },
		generatorSet: { value: false },
		termsAndConditions: {
			value: {
				value: 'avc',
				label: 'AVC',
			},
		},
		bidOnly: { value: !(userSettings?.fixedPrice ?? true) },
		emptyContainer: { value: isEmptyContainer },
		...(defaultShippingLine && {
			shippingLine: { value: defaultShippingLine },
		}),
	};

	return {
		...DEFAULT_ROW_DATA,
		...getDefaultLocationData(transportType),
	};
};
